import React, { useEffect, useRef, useState } from "react";

const SizeDropdown = ({ options = [], value, onChange }) => {
	const [isShowOption, setShowOption] = useState(false);
	const [selectedValue, setSelectedValue] = useState(value);
	const [selectedLabel, setSelectedLabel] = useState("");
	const selectRef = useRef(null);
	const optionRefs = useRef({});

	const handleShowDropdown = () => {
		setShowOption((prev) => !prev);
	};

	const handleOptionClick = (e, value) => {
		e.stopPropagation();
		setShowOption(false);
		onChange && onChange(value);
	};

	const handleOutsideClick = (event) => {
		if (selectRef.current && !selectRef.current.contains(event.target)) {
			setShowOption(false);
		}
	};

	useEffect(() => {
		document.addEventListener("mousedown", handleOutsideClick);
		return () => {
			document.removeEventListener("mousedown", handleOutsideClick);
		};
	}, []);

	// ✅ SAFELY extract label (handles object/string/number)
	const getLabel = (label) => {
		if (label === null || label === undefined) return "";

		if (typeof label === "object") {
			return label.label || label.name || label.value || "Custom";
		}

		return label;
	};

	// ✅ Find selected label safely (supports grouped options)
	useEffect(() => {
		setSelectedValue(value);

		const findLabel = (items) => {
			for (const item of items) {
				// direct match
				if (item?.value?.toString?.() === value?.toString?.() || item?.toString?.() === value?.toString?.()) {
					return getLabel(item?.label ?? item);
				}

				// grouped
				if (item?.options?.length) {
					const nested = findLabel(item.options);
					if (nested) return nested;
				}
			}
			return "";
		};

		setSelectedLabel(findLabel(options));
	}, [value, options]);

	// ✅ Auto scroll to selected
	useEffect(() => {
		if (isShowOption && selectedValue && optionRefs.current[selectedValue]) {
			const optionElement = optionRefs.current[selectedValue];
			const dropdownContainer = selectRef.current?.querySelector(".select-options-wrapper");

			if (dropdownContainer?.children?.[0]) {
				dropdownContainer.children[0].scrollTop = optionElement.offsetTop - 16 - optionElement.getBoundingClientRect().height * 2;
			}
		}
	}, [selectedValue, isShowOption]);

	return (
		<div ref={selectRef} className="dropdown-wrapper" onClick={handleShowDropdown}>
			<div className="select-trigger">{selectedLabel || "Select"}</div>

			{isShowOption && (
				<div className="select-options-wrapper">
					<div className="select-options">
						{options.map((item, index) => {
							// ✅ Normal option
							if (item?.value !== undefined) {
								return (
									<div
										key={`opt-${item.value}`}
										ref={(el) => {
											if (item.value) optionRefs.current[item.value] = el;
										}}
										className={`select-option ${selectedValue?.toString?.() === item.value?.toString?.() ? "active" : ""}`}
										onClick={(e) => handleOptionClick(e, item.value)}
									>
										{getLabel(item.label)}
									</div>
								);
							}

							// ✅ Grouped options
							if (item?.options?.length) {
								return (
									<React.Fragment key={`group-${getLabel(item.label)}-${index}`}>
										<div className="select-group-label">{getLabel(item.label)}</div>

										{item.options.map((groupItem, gIndex) => (
											<div
												key={`opt-${groupItem.value}-${gIndex}`}
												ref={(el) => {
													if (groupItem.value) optionRefs.current[groupItem.value] = el;
												}}
												className={`select-option ${selectedValue?.toString?.() === groupItem.value?.toString?.() ? "active" : ""}`}
												onClick={(e) => handleOptionClick(e, groupItem.value)}
											>
												{getLabel(groupItem.label)}
											</div>
										))}
									</React.Fragment>
								);
							}

							// ✅ Primitive fallback
							return (
								<div
									key={`opt-${index}`}
									ref={(el) => {
										if (item) optionRefs.current[item] = el;
									}}
									className={`select-option ${selectedValue?.toString?.() === item?.toString?.() ? "active" : ""}`}
									onClick={(e) => handleOptionClick(e, item)}
								>
									{getLabel(item)}
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
};

export default SizeDropdown;
